import { checkSession } from "@features/auth";
import { oneDeliveryBundleUrl } from "@utils/oneDeliveryLinks";
import { getUserId, useUserInfo } from "@features/auth/useAuth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { cn } from "@shadcn/lib/utils";
import { Alert, AlertDescription } from "@shadcn/ui/alert";
import { Button } from "@shadcn/ui/button";
import { Card } from "@shadcn/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { createFileRoute, Link, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { Check, Circle, CircleCheck, Clock, GalleryHorizontal, List, Map as MapIcon, MapPin, PackageCheck } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { HubMap } from "~/components/layout/HubMap/HubMap";
import { PageContainer } from "~/components/layout/PageContainer";
import { requireOnboarded } from "~/components/one-delivery/onboarding";
import { ACTIVE_STATUSES, bundleOrdinal, packageStatusLabel } from "~/components/one-delivery/bundle-status";
import { PackageTrack } from "~/components/one-delivery/PackageTrack";
import type { BundleStatus, bundleOneDeliveryQuery } from "./__generated__/bundleOneDeliveryQuery.graphql";
import type { bundleOneDeliveryConfirmMutation } from "./__generated__/bundleOneDeliveryConfirmMutation.graphql";
import type { bundleOneDeliveryDestinationMutation } from "./__generated__/bundleOneDeliveryDestinationMutation.graphql";

const query = graphql`
	query bundleOneDeliveryQuery($id: UUID!, $userId: UUID!, $statuses: [BundleStatus!]!) {
		activeBundles: bundles(filter: { userId: $userId, statuses: $statuses }, pagination: { page: 0, pageSize: 100 }) {
			nodes {
				id
				createdAt
			}
		}
		bundle(id: $id) {
			id
			userId
			status
			hub {
				id
				meta {
					id
					name
					street
					city
				}
			}
			warehouse {
				id
				name
				street
				postalCode
				city
			}
			destinationHubs {
				id
				meta {
					id
					services
				}
			}
			packages(pagination: { page: 0, pageSize: 1000 }) {
				totalCount
				nodes {
					id
					status
					creditId
					updatedAt
					reservation {
						id
						space {
							id
							frame
							position
						}
					}
				}
			}
		}
		creditBalance {
			available
		}
	}
`;

const destinationMutation = graphql`
	mutation bundleOneDeliveryDestinationMutation($input: BundleDestinationSetInput!) {
		bundleDestinationSet(input: $input) {
			id
			hub {
				id
				meta {
					id
					name
					street
					city
				}
			}
		}
	}
`;

const confirmMutation = graphql`
	mutation bundleOneDeliveryConfirmMutation($input: BundleInput!, $notificationUrl: String) {
		bundleConfirm(input: $input, notificationUrl: $notificationUrl) {
			id
			status
		}
	}
`;

export const Route = createFileRoute("/one-delivery/bundle/$bundleId")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context, params }) => {
		const userId = (await checkSession()) ? getUserId() : undefined;
		if (!userId) {
			throw redirect({ to: "/auth", search: { redirectToPath: `/one-delivery/bundle/${params.bundleId}`, show: "sign-in" } });
		}
		await requireOnboarded(context.relayEnvironment);
		return preloadQuery<bundleOneDeliveryQuery>(context.relayEnvironment, query, {
			id: params.bundleId,
			userId,
			statuses: ACTIVE_STATUSES as unknown as BundleStatus[],
		});
	},
});

type Bundle = bundleOneDeliveryQuery["response"]["bundle"];

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const { bundle, creditBalance, activeBundles } = usePreloadedQuery<bundleOneDeliveryQuery>(query, Route.useLoaderData());
	const n = bundleOrdinal(activeBundles.nodes, bundle.id);

	return (
		<PageContainer className="bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery" label={i18n.OD_BACK_TO_SHIPMENTS()} />
			<div className="flex flex-col gap-4 px-6 pt-2 pb-8">
				<h1 className="pt-2 font-breit text-2xl font-normal text-neutral-900">
					{n ? i18n.OD_BUNDLE_TITLE({ index: n }) : i18n.OD_TAB_BUNDLE()}
				</h1>
				<div className="flex justify-between gap-3 text-base font-medium text-black">
					<span>{i18n.OD_PICKUP_LOCATION()}</span>
					<span className="min-w-0 truncate">{bundle.hub?.meta?.name ?? i18n.OD_NO_DESTINATION()}</span>
				</div>
				<PackageTrack count={bundle.packages.totalCount} />

				{bundle.status === "FORMING" && <FormingSection bundle={bundle} availableCredits={creditBalance.available} />}
				{bundle.status === "CONFIRMED" && (
					<Alert>
						<AlertDescription>{i18n.OD_IN_DELIVERY_INFO()}</AlertDescription>
					</Alert>
				)}
				{(bundle.status === "READY_FOR_PICKUP" || bundle.status === "PARTIALLY_COLLECTED") && <PickupSection bundle={bundle} />}

				<hr className="border-neutral-200" />
				<BundlePackages bundle={bundle} />
			</div>
		</PageContainer>
	);
}

/** "Inklusive": the bundle's packages as a swipeable gallery (default) or a list. */
function BundlePackages({ bundle }: { bundle: Bundle }) {
	const i18n = useI18nContext().LL["new-ui"];
	const [view, setView] = useState<"gallery" | "list">("gallery");
	const [active, setActive] = useState(0);
	const packages = bundle.packages.nodes;

	return (
		<section className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<h2 className="text-base font-medium text-neutral-900">{i18n.OD_INCLUDED_TITLE()}</h2>
				<Tabs value={view} onValueChange={(value) => setView(value as "gallery" | "list")}>
					<TabsList className="h-9 rounded-[10px] bg-neutral-100 p-[3px]">
						<TabsTrigger value="gallery" aria-label={i18n.OD_VIEW_GALLERY()} className="rounded-lg px-2 py-1">
							<GalleryHorizontal className="size-4" />
						</TabsTrigger>
						<TabsTrigger value="list" aria-label={i18n.OD_VIEW_LIST()} className="rounded-lg px-2 py-1">
							<List className="size-4" />
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			{view === "gallery" ? (
				<div className="flex flex-col items-center gap-6">
					<div
						className="-mx-6 flex w-[calc(100%+3rem)] snap-x snap-mandatory gap-4 overflow-x-auto px-6 [scrollbar-width:none]"
						onScroll={(event) => {
							const { scrollLeft, clientWidth } = event.currentTarget;
							setActive(Math.round(scrollLeft / (clientWidth - 32)));
						}}
					>
						{packages.map((item) => (
							<div key={item.id} className="w-full shrink-0 snap-center">
								<PackageCard item={item} warehouse={bundle.warehouse} />
							</div>
						))}
					</div>
					{packages.length > 1 && (
						<div className="flex items-center gap-3">
							{packages.map((item, index) => (
								<span
									key={item.id}
									className={cn("h-2 rounded-full transition-all", index === active ? "w-6 bg-neutral-900" : "w-2 bg-neutral-300")}
								/>
							))}
						</div>
					)}
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{packages.map((item) => (
						<PackageCard key={item.id} item={item} warehouse={bundle.warehouse} />
					))}
				</div>
			)}
		</section>
	);
}

type PackageItem = Bundle["packages"]["nodes"][number];

const packageBadgeStyle: Record<string, string> = {
	IN_WAREHOUSE: "border-blue-500 bg-blue-100 text-blue-900",
	IN_TRANSIT_TO_HUB: "border-amber-500 bg-amber-100 text-amber-900",
	IN_COMPARTMENT: "border-green-500 bg-green-100 text-green-900",
	COLLECTED: "border-neutral-400 bg-neutral-100 text-neutral-900",
	IN_TRANSIT_TO_WAREHOUSE: "border-red-500 bg-red-100 text-red-900",
};

/** Package card (Figma "OD - Shipment - In Delivery - Detail Page - Bundle"): origin header and status. */
function PackageCard({ item, warehouse }: { item: PackageItem; warehouse: Bundle["warehouse"] }) {
	const { LL, locale } = useI18nContext();
	const i18n = LL["new-ui"];
	return (
		<div className="overflow-hidden rounded-[20px] border border-neutral-200 bg-white shadow-sm">
			<div className="flex gap-4 bg-black p-5 text-white">
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<p className="break-words font-breit text-3xl font-normal">{warehouse.name}</p>
					<p className="text-xs">
						{warehouse.street}, {warehouse.postalCode} {warehouse.city}
					</p>
				</div>
				<span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-950 shadow-xs">
					<PackageCheck className="size-4" />
				</span>
			</div>
			<div className="flex flex-col gap-3 p-5">
				<div className={cn("flex items-center gap-2 rounded-xl border px-4 py-3.5", packageBadgeStyle[item.status])}>
					<Clock className="size-4 shrink-0" />
					<div className="flex min-w-0 flex-col text-sm">
						<span className="font-medium uppercase">{packageStatusLabel(i18n, item.status)}</span>
						<span>{formatWhen(item.updatedAt, locale, i18n)}</span>
					</div>
				</div>
				<p className="flex gap-1 text-sm">
					<span className="text-neutral-500">{i18n.OD_PACKAGE_ID()}</span>
					<span className="break-all font-medium text-neutral-950">{item.id}</span>
				</p>
			</div>
		</div>
	);
}

function formatWhen(iso: string, locale: string, i18n: ReturnType<typeof useI18nContext>["LL"]["new-ui"]): string {
	const date = new Date(iso);
	const time = date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
	if (date.toDateString() === new Date().toDateString()) return i18n.OD_TODAY_AT({ time });
	return i18n.OD_DATE_AT({ date: date.toLocaleDateString(locale, { day: "numeric", month: "long" }), time });
}

/** Recipient chooses the pickup hub and sends the bundle; sending uses one of their bundles (one Credit per Bundle). */
function FormingSection({ bundle, availableCredits }: { bundle: Bundle; availableCredits: number }) {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const user = useUserInfo();
	const [setDestination, isSettingDestination] = useAsyncMutation<bundleOneDeliveryDestinationMutation>(destinationMutation);
	const [confirm, isConfirming] = useAsyncMutation<bundleOneDeliveryConfirmMutation>(confirmMutation);
	const hubs = bundle.destinationHubs.filter((hub) => hub.meta?.services.includes("ONEDELIVERY"));
	const total = bundle.packages.totalCount;
	const inWarehouse = bundle.packages.nodes.filter((item) => item.status === "IN_WAREHOUSE").length;
	const allInWarehouse = total > 0 && inWarehouse === total;
	// The backend reserves one Credit for the whole Bundle, and none when every package is
	// already covered (e.g. returned packages sent again).
	const needsCredit = bundle.packages.nodes.some((item) => !item.creditId);
	const enoughCredits = !needsCredit || availableCredits > 0;
	const isRecipient = user?.id === bundle.userId;
	const busy = isSettingDestination || isConfirming;
	const [mapOpen, setMapOpen] = useState(false);

	/** Returns whether the Hub is now the destination, so the map stays open after a failure. */
	const chooseHub = async (hubId: string) => {
		if (busy) return false;
		if (hubId === bundle.hub?.id) return true;
		try {
			await setDestination({ variables: { input: { bundleId: bundle.id, hubId } } });
			return true;
		} catch (error) {
			console.error(error);
			toast.error(i18n.OD_DESTINATION_FAILED());
			return false;
		}
	};

	const send = async () => {
		try {
			await confirm({ variables: { input: { bundleId: bundle.id }, notificationUrl: oneDeliveryBundleUrl(bundle.id) } });
			toast.success(i18n.OD_BUNDLE_CONFIRMED());
			await router.invalidate();
		} catch (error) {
			console.error(error);
			toast.error(i18n.OD_CONFIRM_FAILED());
		}
	};

	return (
		<section className="flex flex-col gap-4">
			<h2 className="font-breit text-lg font-medium">
				<T>{i18n.OD_CHOOSE_HUB()}</T>
			</h2>
			{bundle.hub?.meta && (
				<div className="flex items-start gap-3 rounded-[14px] border border-neutral-200 bg-white p-4">
					<MapPin className="mt-0.5 size-4 shrink-0" />
					<div className="min-w-0">
						<p className="break-words font-medium">{bundle.hub.meta.name}</p>
						{bundle.hub.meta.street && (
							<p className="break-words text-sm text-muted-foreground">
								{bundle.hub.meta.street}, {bundle.hub.meta.city}
							</p>
						)}
					</div>
				</div>
			)}
			<Button variant="outline" size="lg" className="w-full" disabled={busy} onClick={() => setMapOpen(true)}>
				<MapIcon className="size-4" />
				<T>{bundle.hub ? i18n.OD_CHANGE_ON_MAP() : i18n.OD_CHOOSE_ON_MAP()}</T>
			</Button>
			<Suspense fallback={null}>
				<HubMap
					open={mapOpen}
					onOpenChange={setMapOpen}
					hubIds={hubs.map((hub) => hub.id)}
					filterService="ONEDELIVERY"
					currentHubMetaName={bundle.hub?.meta?.name}
					renderAction={({ id }, close) => (
						<Button
							size="lg"
							className="w-full"
							disabled={busy}
							onClick={async () => {
								if (await chooseHub(id)) close();
							}}
						>
							{id === bundle.hub?.id ? <Check className="size-4" /> : null}
							<T>{i18n.OD_CHOOSE_THIS_HUB()}</T>
						</Button>
					)}
				/>
			</Suspense>
			{/* Always explain what sending the bundle requires and which steps are still missing. */}
			<div className="flex flex-col gap-3 rounded-[14px] border border-neutral-200 bg-white p-4">
				<p className="font-medium">{i18n.OD_FORMING_TITLE()}</p>
				<p className="text-sm text-muted-foreground">
					<T>{i18n.OD_CONFIRM_DESCRIPTION()}</T>
				</p>
				<ul className="flex flex-col gap-3">
					<Requirement done={!!bundle.hub}>
						{bundle.hub?.meta ? i18n.OD_FORMING_HUB_DONE({ name: bundle.hub.meta.name }) : i18n.OD_FORMING_HUB_TODO()}
					</Requirement>
					<Requirement done={allInWarehouse}>
						{allInWarehouse
							? i18n.OD_FORMING_PACKAGES_DONE({ count: total })
							: i18n.OD_FORMING_PACKAGES_TODO({ arrived: inWarehouse, total })}
					</Requirement>
					<Requirement done={enoughCredits}>
						<span className="flex flex-col gap-1">
							{!enoughCredits
								? i18n.OD_NOT_ENOUGH_CREDITS()
								: needsCredit
									? i18n.OD_FORMING_CREDITS_DONE({ available: availableCredits })
									: i18n.OD_FORMING_CREDITS_COVERED()}
							{!enoughCredits && (
								<Link to="/one-delivery/credits" search={{ bundleId: bundle.id }} className="font-medium text-neutral-950 underline">
									{i18n.OD_CREDITS_BUY()}
								</Link>
							)}
						</span>
					</Requirement>
				</ul>
				{!isRecipient && <p className="text-sm text-muted-foreground">{i18n.OD_FORMING_NOT_RECIPIENT()}</p>}
			</div>
			{isRecipient && (
				<Button
					size="lg"
					className="w-full"
					disabled={busy || !bundle.hub || !allInWarehouse || !enoughCredits}
					onClick={() => void send()}
				>
					<T>{i18n.OD_CONFIRM_BUNDLE()}</T>
				</Button>
			)}
		</section>
	);
}

function Requirement({ done, children }: { done: boolean; children: ReactNode }) {
	const Icon = done ? CircleCheck : Circle;
	return (
		<li className="flex items-start gap-2 text-sm">
			<Icon className={cn("mt-0.5 size-4 shrink-0", done ? "text-green-600" : "text-neutral-400")} />
			<span className={cn("min-w-0", !done && "text-neutral-950")}>{children}</span>
		</li>
	);
}

/** One card per compartment holding this bundle's packages; each opens its own door. */
function PickupSection({ bundle }: { bundle: Bundle }) {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const compartments = [
		...new Map(
			bundle.packages.nodes.flatMap((item) =>
				item.status === "IN_COMPARTMENT" && item.reservation ? [[item.reservation.id, item.reservation] as const] : []
			)
		).values(),
	];

	return (
		<section className="flex flex-col gap-4">
			<p className="text-sm text-muted-foreground">
				<T>{i18n.OD_PICKUP_INFO()}</T>
			</p>
			{compartments.map((reservation) => {
				const count = bundle.packages.nodes.filter(
					(item) => item.status === "IN_COMPARTMENT" && item.reservation?.id === reservation.id
				).length;
				return (
					<Card key={reservation.id} className="gap-4 rounded-[14px] p-5 shadow-sm">
						<div className="flex items-center justify-between gap-3">
							<p className="font-breit text-lg font-medium">
								{i18n.OD_COMPARTMENT({ name: `${reservation.space.frame}-${reservation.space.position}` })}
							</p>
							<span className="text-sm text-muted-foreground">
								{count} {i18n.OD_PACKAGES()}
							</span>
						</div>
						<Button
							size="lg"
							className="w-full"
							onClick={() =>
								navigate({
									to: "/one-delivery/pickup",
									search: { bundleId: bundle.id, reservationId: reservation.id },
								})
							}
						>
							<T>{i18n.OD_PICK_UP_NOW()}</T>
						</Button>
					</Card>
				);
			})}
		</section>
	);
}
